import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Bug, ChevronDown, ChevronUp } from "lucide-react";
import { fetchErrors } from "@/lib/queries";
import { ago, dateTime, number } from "@/lib/format";
import type { ErrorEvent } from "@/lib/types";

type Filter = "all" | ErrorEvent["severity"];

const filters: Array<{ key: Filter; label: string }> = [
  { key: "all", label: "All" },
  { key: "fatal", label: "Fatal" },
  { key: "error", label: "Error" },
  { key: "warning", label: "Warning" },
];

export function ErrorsPage() {
  const [filter, setFilter] = useState<Filter>("all");
  const [open, setOpen] = useState<number | null>(null);
  const errors = useQuery({
    queryKey: ["errors", filter],
    queryFn: () => fetchErrors(filter === "all" ? undefined : filter),
  });

  return (
    <>
      <div className="page-head">
        <div>
          <h2 style={{ fontSize: 15 }}>What broke, where</h2>
          <p>Reports from the app and Edge Functions. Retained for 30 days.</p>
        </div>
        <div className="segmented">
          {filters.map((item) => (
            <button
              key={item.key}
              className={filter === item.key ? "active" : ""}
              onClick={() => setFilter(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <section className="card">
        <div className="card-head">
          <div>
            <h2>Error log</h2>
            <p>{number(errors.data?.length ?? 0)} in this view</p>
          </div>
          <div className="spacer" />
          <span className="badge badge-rose">
            <Bug size={12} />
            newest first
          </span>
        </div>

        <div style={{ marginTop: 12 }}>
          {errors.isLoading && <div className="loading-block"><div className="spinner" /></div>}
          {!errors.isLoading && (errors.data ?? []).length === 0 && (
            <div className="empty">
              <div className="empty-icon"><Bug size={18} /></div>
              No errors in this view.
            </div>
          )}
          {(errors.data ?? []).map((error) => (
            <div className="error-row" key={error.id}>
              <div className="row-between">
                <div className="row">
                  <span className={`badge ${error.severity === "fatal" ? "badge-danger" : error.severity === "warning" ? "badge-sunshine" : "badge-rose"}`}>
                    {error.severity}
                  </span>
                  <span className="cell-strong" style={{ fontSize: 12 }}>{error.message}</span>
                </div>
                <button
                  className="btn btn-quiet btn-sm"
                  onClick={() => setOpen(open === error.id ? null : error.id)}
                >
                  {open === error.id ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                  {open === error.id ? "Hide" : "Details"}
                </button>
              </div>
              <div className="tiny faint">
                {dateTime(error.occurred_at)} · {ago(error.occurred_at)} · {error.screen ?? "unknown screen"} ·{" "}
                {error.app_version ?? "—"} · {error.device_model ?? "—"} · {error.os_version ?? "—"}
              </div>
              {open === error.id && error.stack && (
                <pre className="error-stack">{error.stack}</pre>
              )}
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
